/* ==========================================================================
   SofaStyle shared carousel component
   Used by sections/ss-reviews-carousel.liquid and sections/ss-social-feed.liquid.

   Progressive enhancement: the track is a scroll-snap list that is already
   swipeable without this script. The component only adds the arrow buttons,
   the "current-total" counter, the progress bar and the current-slide
   marker on top of it.

   Attributes:
     dim-inactive  Marks the current slide with [data-ss-current] so CSS can
                   fade the slides that aren't the active one. Without this
                   attribute no slide state is written at all.

   State written by the component:
     data-overflowing  Present while there are more slides than fit on screen
                       (the track can scroll). CSS uses it to show the
                       progress bar only when there is something to scroll to.
   ========================================================================== */

class SSCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-ss-carousel-track]');
    if (!this.track) return;

    this.prevButton = this.querySelector('[data-ss-carousel-prev]');
    this.nextButton = this.querySelector('[data-ss-carousel-next]');
    this.counter = this.querySelector('[data-ss-carousel-counter]');
    this.progressBar = this.querySelector('[data-ss-carousel-progress-bar]');
    this.dimInactive = this.hasAttribute('dim-inactive');

    this.handleScroll = this.handleScroll.bind(this);
    this.update = this.update.bind(this);

    if (this.prevButton) this.prevButton.addEventListener('click', () => this.page(-1));
    if (this.nextButton) this.nextButton.addEventListener('click', () => this.page(1));
    this.track.addEventListener('scroll', this.handleScroll, { passive: true });

    /* Tabbing into a dimmed slide should bring it forward rather than leaving
       focus on something the user can barely read. */
    if (this.dimInactive) {
      this.track.addEventListener('focusin', (event) => {
        const slide = event.target.closest('[data-ss-carousel-track] > *');
        if (slide) slide.scrollIntoView({ block: 'nearest', inline: 'start' });
      });
    }

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.track);
    }

    this.setAttribute('data-enhanced', 'true');
    this.update();
  }

  disconnectedCallback() {
    if (this.track) this.track.removeEventListener('scroll', this.handleScroll);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  get slides() {
    return Array.from(this.track.children);
  }

  /* Distance between the start of one slide and the next, gap included. */
  get slideStep() {
    const slides = this.slides;
    if (slides.length < 2) return this.track.clientWidth;
    const step = Math.round(slides[1].offsetLeft - slides[0].offsetLeft);
    return step > 0 ? step : this.track.clientWidth;
  }

  page(direction) {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollBy({
      left: this.slideStep * direction,
      behavior: reduced ? 'auto' : 'smooth'
    });
  }

  handleScroll() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  update() {
    if (!this.track) return;

    const slides = this.slides;
    const total = slides.length;
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const scrolled = Math.abs(this.track.scrollLeft);
    const currentIndex = Math.min(
      Math.max(total - 1, 0),
      Math.max(0, Math.round(scrolled / this.slideStep))
    );

    this.toggleAttribute('data-overflowing', maxScroll > 1);

    if (this.prevButton) this.prevButton.disabled = scrolled <= 1;
    if (this.nextButton) this.nextButton.disabled = scrolled >= maxScroll - 1;

    if (this.counter) {
      this.counter.textContent = total > 0 ? currentIndex + 1 + '-' + total : '';
    }

    if (this.dimInactive) {
      slides.forEach((slide, index) => {
        slide.toggleAttribute('data-ss-current', index === currentIndex);
      });
    }

    if (this.progressBar) {
      const visibleRatio = this.track.scrollWidth > 0
        ? Math.min(1, this.track.clientWidth / this.track.scrollWidth)
        : 1;
      const progress = maxScroll > 0 ? scrolled / maxScroll : 0;
      const widthPercent = visibleRatio * 100;
      this.progressBar.style.setProperty('--ss-progress-width', widthPercent + '%');
      this.progressBar.style.setProperty('--ss-progress-offset', progress * (100 - widthPercent) + '%');
    }
  }
}

if (!customElements.get('ss-carousel')) {
  customElements.define('ss-carousel', SSCarousel);
}
